import { useLocation, useNavigate } from "react-router";
import { getMoreMenuData } from "./menus";
import { useVisitorInfo } from "@/common/hooks";
import { useCallback, useMemo } from "react";
import { MoreMenu } from "./MoreMenu";
import { metricsBus } from "@/common/buses";
import {
  onCloseMoreMenu,
  onOpenAddCollectionPopup,
  onOpenAddNichePopup,
  onOpenEditGifNichesPopup,
  onOpenEditGifPreferencesPopup,
  onOpenReportPopup,
  onOpenShareGifMenu,
  onOpenEditGifPopup,
} from "@/components/popup-dispatcher/PopupDispatcher";
import { apiMain } from "@/common/services";
import { IMoreMenuContainerProps, ScrollSectionEnum, Roles } from "@/common/buses/popups/popup-types";
import { useGetMyNiches } from "@/common/queries/queries";

export const MoreMenuContainer: React.FC<IMoreMenuContainerProps> = ({
  gif,
  position,
  playCount,
  anchorEl,
  hideGif,
  onOpenTagFeedbackMode
}) => {
  const currentUser = useVisitorInfo();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: myNichesData } = useGetMyNiches({ previews: true });

  const isNicheContentModerator = useMemo(() => {
    const pathNameSplit = location.pathname.split("/");
    const nicheId = pathNameSplit.pop();
    const pageName = pathNameSplit.pop();
    const myNiches = myNichesData?.niches || [];
    let isModerator = false;

    if (pageName !== "niches") {
      return isModerator;
    }

    myNiches.forEach(({ id }) => {
      if (id === nicheId) {
        isModerator = true;
      }
    });

    return isModerator;
  }, [location, myNichesData]);

  const handleClose = useCallback(() => {
    onCloseMoreMenu();
  }, []);

  const handleHideGif = useCallback(async () => {
    try {
      await apiMain.hideGif({
        id: gif.id,
        home: true,
        trending: true,
      });

      hideGif(gif.id);
      console.debug(`Gif ${gif.id} hidden successfully.`);
    } catch (e) {
      console.error(`Failed to hide gif ${gif.id}.`);
    }

    handleClose();
  }, [gif.id, handleClose, hideGif]);

  const handleManageClick = useCallback(() => {
    handleClose();
    navigate(`/profile/manage?gifId=${gif.id}`);
  }, [navigate, gif.id, handleClose]);

  const handleOpenEditNichesDialog = useCallback((role: Roles) => {
    onOpenEditGifNichesPopup({ gifId: gif.id, role });
    handleClose();
  }, [gif.id, handleClose]);

  const toggleCollection = useCallback(() => {
    onOpenAddCollectionPopup({ gifId: gif.id, boosted: !!gif.cta, position, playCount });
    handleClose();
  }, [gif, handleClose, position, playCount]);

  const toggleNiche = useCallback(() => {
    onOpenAddNichePopup({ gifId: gif.id });
    handleClose();
  }, [gif.id, handleClose]);

  const toggleReport = useCallback(() => {
    onOpenReportPopup({ gifId: gif.id });
    handleClose();
  }, [gif.id, handleClose]);

  const handleOpenEditGif = useCallback(() => {
    onOpenEditGifPopup({ gif, scrollToSection: ScrollSectionEnum.MANAGE_LINKS });
    handleClose();
  }, [gif, handleClose]);

  const handleOpenTagFeedback = () => {
    onOpenTagFeedbackMode?.();
    handleClose();
    metricsBus.emit("tagFeedbackEnabledOptions");
  };

  const toggleShareMenu = useCallback(() => {
    metricsBus.emit("shareClick");

    onOpenShareGifMenu({
      gif: gif,
      position,
      playCount,
      anchorEl: anchorEl,
    });

    handleClose();
  }, [anchorEl, gif, handleClose, position, playCount]);

  const toggleGifPrefs = useCallback(() => {
    onOpenEditGifPreferencesPopup({
      gifId: gif.id,
      sexuality: gif.sexuality || []
    });
    handleClose();
  }, [handleClose, gif]);

  const actions = {
    handleHideGif,
    handleManageClick,
    handleOpenEditNichesDialog,
    handleOpenEditGif,
    handleOpenTagFeedback,
    toggleCollection,
    toggleNiche,
    toggleReport,
    toggleShareMenu,
    toggleGifPrefs,
  };

  const moreMenuData = getMoreMenuData({
    actions,
    currentUser,
    gif: gif,
    isNicheContentModerator,
    isFeedbackEnabled: !!onOpenTagFeedbackMode,
  });

  return (
    <MoreMenu
      isOpen
      anchorEl={anchorEl}
      onClose={handleClose}
      menuItems={moreMenuData}
    />
  );
};
